import React, { useEffect, useCallback, useState } from 'react';
import Table from '../../../components/templates/Table/Table';
import { useLoading } from '../../../providers/LoadingProvider';
import { useThemedComponent } from '../../../providers/ThemeProvider';
import {
  type CustomTableColumns,
  SortOrder,
  InternalUserRolesColumns,
} from '../../../types/tables';
import {
  ROLE_COLUMNS,
  formatAllNonSubjectUsers,
  transformForExport,
} from './helpers';
import SetUserRoleStyles from './SetInternalUserRolesStyles';
import { ButtonRow } from '../../../components/atoms/ButtonRow/ButtonRow';
import i18n from '../../../translations';
import {
  KNOWN_CLOUD_FUNCTIONS,
  useCloudContext,
} from '../../../providers/CloudProvider';
import {
  GetNonSubjectUsersResponse,
  InternalUserRoles,
  RoleOperation,
} from '../../../types/cloud';
import { useTableColumns } from '../../../hooks/useTableColumns';
import DropDownMultiSelect from '../../../components/atoms/DropDownSelect/DropDownMultiSelect';
import { commonTableStyles } from '../../../components/templates/Table/tableUtil';

/**
 * Should only be visible to internal creation managers
 * @returns
 */
export default function SetInternalUserRoles() {
  const { styles } = useThemedComponent([commonTableStyles, SetUserRoleStyles]);
  const { setLoading } = useLoading();
  const { cloudService } = useCloudContext();
  const {
    columnHelper,
    tableData,
    setTableData,
    rowSelection,
    setRowSelection,
    sorting,
    setSorting,
    ExportDropDown,
  } = useTableColumns<InternalUserRolesColumns>({
    defaultSorting: [
      { colKey: 'internalUser', index: 0, order: SortOrder.ASC },
    ],
    tableName: i18n.t('setInternalUserRoles.title'),
    transformExportData: transformForExport,
  });
  const [grantRoleSelectionIndexes, setGrantRoleSelectionIndexes] = useState<
    number[]
  >([]);
  const [revokeRoleSelectionIndexes, setRevokeRoleSelectionIndexes] = useState<
    number[]
  >([]);

  /**
   * Fetch data for table
   */
  const dataFetcher = useCallback(async () => {
    setLoading(true);
    try {
      const nonSubjectUsers = await cloudService.run<
        Promise<GetNonSubjectUsersResponse>
      >(KNOWN_CLOUD_FUNCTIONS.GET_NON_SUBJECT_USERS);

      const fetchedData = await formatAllNonSubjectUsers(nonSubjectUsers);
      if (fetchedData) {
        setTableData(fetchedData);
      }
    } catch (e) {
      alert(`Failed to get non-subject users ${e}`);
    } finally {
      setLoading(false);
    }
  }, [cloudService, setLoading, setTableData]);

  useEffect(() => {
    dataFetcher();
  }, [dataFetcher]);

  /**
   * Grant or Revoke Internal Roles on selected users
   */
  const onSelectRoles = useCallback(
    async (operation: RoleOperation) => {
      const selectionIndexes =
        operation === RoleOperation.ADD
          ? grantRoleSelectionIndexes
          : revokeRoleSelectionIndexes;
      if (
        selectionIndexes.length === 0 ||
        Object.keys(rowSelection).length === 0
      ) {
        return;
      }
      for (const userId of Object.keys(rowSelection)) {
        await cloudService.run(
          KNOWN_CLOUD_FUNCTIONS.GRANT_OR_REVOKE_INTERNAL_ROLE,
          JSON.parse(
            JSON.stringify({
              userId,
              operation,
              roles: Object.values(InternalUserRoles).filter((_, index) =>
                selectionIndexes.includes(index),
              ),
            }),
          ),
        );
      }
      await dataFetcher();
      operation === RoleOperation.ADD
        ? setGrantRoleSelectionIndexes([])
        : setRevokeRoleSelectionIndexes([]);
    },
    [
      grantRoleSelectionIndexes,
      revokeRoleSelectionIndexes,
      rowSelection,
      dataFetcher,
      cloudService,
    ],
  );

  /**
   * Helper to get row id for row selection
   */
  const getRowId = (row: CustomTableColumns, index: number): string => {
    return row.id ?? index.toString();
  };

  return (
    <div style={styles.pageContainer}>
      <div style={styles.pageHeader}>
        <h1 style={styles.h1}>{i18n.t('setInternalUserRoles.title')}</h1>
        <div style={styles.btnGroup}>
          <DropDownMultiSelect
            options={Object.values(InternalUserRoles)}
            labels={Object.values(InternalUserRoles).map(r =>
              i18n.t(`setInternalUserRoles.roles.${r}`),
            )}
            selectedIndexes={grantRoleSelectionIndexes}
            setSelectedIndexes={setGrantRoleSelectionIndexes}
            placeholder={i18n.t('setInternalUserRoles.setRole')}
            style={styles.dropdownButton}
            menuStyle={styles.menu}
            extraChildren={
              <ButtonRow
                buttons={[
                  {
                    title: i18n.t('globals.cancel'),
                    onClick: () => {
                      setGrantRoleSelectionIndexes([]);
                    },
                    style: { ...styles.itemBtn, ...styles.transparentBg },
                  },
                  {
                    title: i18n.t('globals.set'),
                    onClick: () => onSelectRoles(RoleOperation.ADD),
                    style: { ...styles.itemBtn },
                  },
                ]}
                style={styles.itemBtnContainer}
              />
            }
          />

          <DropDownMultiSelect
            options={Object.values(InternalUserRoles)}
            labels={Object.values(InternalUserRoles).map(r =>
              i18n.t(`setInternalUserRoles.roles.${r}`),
            )}
            selectedIndexes={revokeRoleSelectionIndexes}
            setSelectedIndexes={setRevokeRoleSelectionIndexes}
            placeholder={i18n.t('setInternalUserRoles.removeRole')}
            style={styles.dropdownButton}
            menuStyle={styles.menu}
            extraChildren={
              <ButtonRow
                buttons={[
                  {
                    title: i18n.t('globals.cancel'),
                    onClick: () => {
                      setRevokeRoleSelectionIndexes([]);
                    },
                    style: { ...styles.itemBtn, ...styles.transparentBg },
                  },
                  {
                    title: i18n.t('globals.remove'),
                    onClick: () => onSelectRoles(RoleOperation.REMOVE),
                    style: { ...styles.itemBtn },
                  },
                ]}
                style={styles.itemBtnContainer}
              />
            }
          />
          <ExportDropDown style={styles.exportButton} />
        </div>
      </div>
      <div style={{ ...styles.rowContainer, ...styles.lastRow }}>
        <Table
          columnHelper={columnHelper}
          columns={ROLE_COLUMNS}
          sorting={sorting}
          setSorting={setSorting}
          data={tableData}
          selectable={true}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          getRowId={getRowId}
        />
      </div>
    </div>
  );
}
